import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectorRef, forwardRef, OnDestroy } from '@angular/core';
import { Site, WorkingHours } from '@app/graphql-typings/graphql';
import { UntypedFormArray, UntypedFormGroup, UntypedFormBuilder, ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validator, AbstractControl, ValidationErrors } from '@angular/forms';
import { PickerSchedule } from './time-picker-day/time-picker-day.component';
import { AddEditUtility } from '@app/layout/sites/manage/manage-sites/add-edit-site/add-edit-utility';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CalendarUtilsService } from "@shared/services";

@Component({
  selector: 'app-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TimePickerComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => TimePickerComponent), multi: true}
  ],
  standalone: false
})
export class TimePickerComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {

  readonly emptyWorkingHours = {
    'mondayEnabled': true,
    'mondayStart': '00:00',
    'mondayEnd': '00:00',
    'tuesdayEnabled': true,
    'tuesdayStart': '00:00',
    'tuesdayEnd': '00:00',
    'wednesdayEnabled': true,
    'wednesdayStart': '00:00',
    'wednesdayEnd': '00:00',
    'thursdayEnabled': true,
    'thursdayStart': '00:00',
    'thursdayEnd': '00:00',
    'fridayEnabled': true,
    'fridayStart': '00:00',
    'fridayEnd': '00:00',
    'saturdayEnabled': true,
    'saturdayStart': '00:00',
    'saturdayEnd': '00:00',
    'sundayEnabled': true,
    'sundayStart': '00:00',
    'sundayEnd': '00:00'
  };

  @Input() readonly = false;
  @Output() readonlyChange = new EventEmitter<boolean>();
  @Output() siteChange = new EventEmitter<Site>();
  workingHours: WorkingHours;
  form: UntypedFormGroup;
  schedule: PickerSchedule[] = this.createEmptySchedule();
  scheduleFormControls: UntypedFormArray = this.fb.array([]);
  formReady = false;
  delayedOnChange = false;
  delayedDisableEnableFn: () => void;

  private readonly ngUnsubscribe: Subject<void> = new Subject();
  private readonly DAYS_CONTROL_NAME = 'days';
  private onChange = null;
  private onTouched = () => {};

  constructor(
    private fb: UntypedFormBuilder,
    private cdRef: ChangeDetectorRef,
    private calendarUtils: CalendarUtilsService
  ) {
    this.form = this.fb.group({
      [this.DAYS_CONTROL_NAME]: this.scheduleFormControls
    });
  }

  ngOnInit() {
    this.form.get(this.DAYS_CONTROL_NAME).valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((formData: any) => {
      if (this.formReady && formData.length === 7) {
        this.onChange(this.convertFormToWorkingHours(formData));
        this.onTouched();
      }
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  writeValue(workingHours: WorkingHours): void {
    if (workingHours == null || !this.isWorkingHours(workingHours)) {
      this.workingHours = this.emptyWorkingHours;
      if (this.onChange) {
        this.onChange(this.workingHours);
      } else {
        this.delayedOnChange = true;
      }
    } else {
      this.workingHours = workingHours;
      this.updateWorkingHoursControl(this.DAYS_CONTROL_NAME);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!this.scheduleFormControls || !this.scheduleFormControls.controls || this.scheduleFormControls.controls.length !== 7) {
      this.delayedDisableEnableFn = () => this.setDisabled(isDisabled);
    } else {
      this.setDisabled(isDisabled);
    }
  }

  validate(c: AbstractControl): ValidationErrors | null {
    if (this.form.get(this.DAYS_CONTROL_NAME).valid) {
      return null;
    } else {
      return {invalid: true};
    }
  }

  addScheduleFormControl(index: number, last: boolean, formGroup: UntypedFormGroup) {
    this.scheduleFormControls.insert(index, formGroup);
    if (last) {
      this.populateForm();
    }
  }

  copyDaySchedule(event) {
    const schedule = event.schedule;
    const singleDay = event.singleDay;
    const direction = event.direction;
    const index = event.index;
    const scheduleControls = this.getScheduleGroupControls();

    scheduleControls.map((s, i) => {
      if (singleDay) {
        if (index > 0 && direction === 'left' && i === index - 1) {
          s.setValue(schedule);
        }
        if (index < this.calendarUtils.DAYS_OF_THE_WEEK.length && direction === 'right' && i === index + 1) {
          s.setValue(schedule);
        }
      } else {
        if (index > 0 && direction === 'left' && i < index) {
          s.setValue(schedule);
        }
        if (index < this.calendarUtils.DAYS_OF_THE_WEEK.length && direction === 'right' && i > index) {
          s.setValue(schedule);
        }
      }
    });
  }

  private setDisabled(isDisabled: boolean) {
    for (let i = 0; i < this.scheduleFormControls.controls.length; i++) {
      const control = this.scheduleFormControls.controls[i];
      if (isDisabled) {
        if (control.enabled) {
          control.disable({emitEvent: false});
        }
      } else if (control.disabled) {
        control.enable({emitEvent: false});
      }
    }
  }

  private createEmptySchedule(): PickerSchedule[] {
    const scheduleArray: PickerSchedule[] = [];
    for (let i = 0; i < 7; i++) {
      scheduleArray.push({
        enabled: true,
        start: {hour: 0, minute: 0, second: 0},
        end: {hour: 0, minute: 0, second: 0}
      });
    }
    return scheduleArray;
  }

  private getScheduleGroupControls() {
    return (<UntypedFormArray>this.form.get(this.DAYS_CONTROL_NAME)).controls;
  }

  private updateWorkingHoursControl(controlName: string) {
    const formArray: UntypedFormArray = <UntypedFormArray>this.form.get(controlName);
    if (formArray?.controls?.length === 7) { // OR use this.formReady instead or find a way of avoiding this check completely?
      const days: string[] =  this.calendarUtils.DAYS_OF_THE_WEEK.map((day: string) => day.toLowerCase());
      if (this.workingHours !== null) {
        for (let i = 0; i < 7; i++) {
          formArray.controls[i].setValue({
              enabled: this.workingHours[days[i] + 'Enabled'],
              start: AddEditUtility.stringToNgbTime(this.workingHours[days[i] + 'Start']),
              end: AddEditUtility.stringToNgbTime(this.workingHours[days[i] + 'End'])
            }
            );
        }
      } else {
        for (let i = 0; i < 7; i++) {
          formArray.controls[i].setValue({
              enabled: true,
              start: {hour: 0, minute: 0, second: 0},
              end: {hour: 0, minute: 0, second: 0},
            }
          );
        }
      }
    }
  }

  private populateForm() {
    this.updateWorkingHoursControl(this.DAYS_CONTROL_NAME);
    this.formReady = true;

    if (this.delayedOnChange) {
      this.delayedOnChange = false;
      this.onChange(this.workingHours);
    }

    if (this.delayedDisableEnableFn) {
      this.delayedDisableEnableFn();
      this.delayedDisableEnableFn = null;
    }
    this.cdRef.detectChanges();
  }

  private convertFormToWorkingHours(formData: any): WorkingHours {
    return {
      'mondayEnabled': formData[0].enabled,
      'mondayStart': AddEditUtility.ngbTimeToString(formData[0].start),
      'mondayEnd': AddEditUtility.ngbTimeToString(formData[0].end),
      'tuesdayEnabled': formData[1].enabled,
      'tuesdayStart': AddEditUtility.ngbTimeToString(formData[1].start),
      'tuesdayEnd': AddEditUtility.ngbTimeToString(formData[1].end),
      'wednesdayEnabled': formData[2].enabled,
      'wednesdayStart': AddEditUtility.ngbTimeToString(formData[2].start),
      'wednesdayEnd': AddEditUtility.ngbTimeToString(formData[2].end),
      'thursdayEnabled': formData[3].enabled,
      'thursdayStart': AddEditUtility.ngbTimeToString(formData[3].start),
      'thursdayEnd': AddEditUtility.ngbTimeToString(formData[3].end),
      'fridayEnabled': formData[4].enabled,
      'fridayStart': AddEditUtility.ngbTimeToString(formData[4].start),
      'fridayEnd': AddEditUtility.ngbTimeToString(formData[4].end),
      'saturdayEnabled': formData[5].enabled,
      'saturdayStart': AddEditUtility.ngbTimeToString(formData[5].start),
      'saturdayEnd': AddEditUtility.ngbTimeToString(formData[5].end),
      'sundayEnabled': formData[6].enabled,
      'sundayStart': AddEditUtility.ngbTimeToString(formData[6].start),
      'sundayEnd': AddEditUtility.ngbTimeToString(formData[6].end)
    };
  }

  private isWorkingHours(workingHours: any) {
    return workingHours.hasOwnProperty('mondayEnabled') && workingHours.hasOwnProperty('thursdayStart') && workingHours.hasOwnProperty('sundayEnd');
  }

  getDayOfTheWeek(index): string {
    return this.calendarUtils.DAYS_OF_THE_WEEK[index];
  }

}
